// YOSHI-10464: the language every multi-language code block of the portal shows.
//
// It is one shared choice and not one per block: an integrator who works in C# picks it once and
// the whole portal answers in C#, instead of clicking the same tab again on every endpoint of the
// reference. The state lives in a context because the blocks are scattered across pages and sit
// several levels deep inside them, so neither a prop nor a module variable would do: a prop would
// be threaded through every component in between, and a module variable would not re-render.

import {
  FC,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  DocsCodeLanguage,
  DocsLanguageContextValue,
  DocsLanguageProviderProps,
} from "../interfaces";
import { DEFAULT_DOCS_CODE_LANGUAGE, DOCS_CODE_LANGUAGES } from "../utils";

const STORAGE_KEY: string = "yoshi-docs-code-language";

/**
 * Reads the remembered choice.
 *
 * Every access to localStorage is guarded: it throws outright in a private window and whenever the
 * browser blocks site data. This is documentation, so a portal that will not render because it
 * could not remember a tab would be a far worse bug than forgetting the tab.
 */
const readStoredLanguage = (): DocsCodeLanguage => {
  try {
    const stored: string | null = window.localStorage.getItem(STORAGE_KEY);
    const isKnown: boolean =
      stored !== null &&
      DOCS_CODE_LANGUAGES.indexOf(stored as DocsCodeLanguage) !== -1;

    if (isKnown) {
      return stored as DocsCodeLanguage;
    }

    return DEFAULT_DOCS_CODE_LANGUAGE;
  } catch (error) {
    return DEFAULT_DOCS_CODE_LANGUAGE;
  }
};

// The default value keeps a block rendered outside the provider working: it shows the default
// language and its tabs simply do nothing, which is better than throwing.
const DocsLanguageContext = createContext<DocsLanguageContextValue>({
  codeLanguage: DEFAULT_DOCS_CODE_LANGUAGE,
  setCodeLanguage: () => undefined,
});

export const DocsLanguageProvider: FC<DocsLanguageProviderProps> = (props) => {
  const { children } = props;
  const [codeLanguage, setCodeLanguage] =
    useState<DocsCodeLanguage>(readStoredLanguage);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, codeLanguage);
    } catch (error) {
      // Same story as reading: the choice still applies to this visit, it just will not survive
      // the next reload. Nothing to report to the reader.
      return;
    }
  }, [codeLanguage]);

  const value = useMemo((): DocsLanguageContextValue => {
    return { codeLanguage, setCodeLanguage };
  }, [codeLanguage]);

  return (
    <DocsLanguageContext.Provider value={value}>
      {children}
    </DocsLanguageContext.Provider>
  );
};

export const useDocsLanguage = (): DocsLanguageContextValue => {
  return useContext(DocsLanguageContext);
};
